import React, {useEffect, useState, useMemo} from "react";
import { useSearchParams } from "react-router-dom";
import {Button, Container, Typography, Box, Collapse, Grid, Alert, AlertTitle, CircularProgress} from "@mui/material";
import {OdpovedSkeleton} from "../components/skeletons";
import ErrorWithRetry from "../components/ErrorWithRetry";
import {useQuery} from "@tanstack/react-query";
import JsdhNotifikaceService from "../api/JsdhNotifikaceService/service/JsdhNotifikaceService";
import {useOdpovedMutation} from "../api/JsdhNotifikaceService/service/JsdhNotifikaceMutation";
import {Odpoved, Udalost} from "../api/JsdhNotifikaceService/generated";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {formatDateTime, getSafeUrl} from "../utils/utils";
import {QueryError} from "../api/QueryError";
import {CasOdVyhlaseni} from "../components/CasOdVyhlaseni";
import AppearanceMenu from "../components/AppearanceMenu";
import { formatAdresa } from "../utils/adresaUdalosti";

const OdpovedPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [open, setOpen] = useState(false);
    const [openDetailOdpovedi, setOpenDetailOdpovedi] = useState(false);
    const [casOdVyhlaseni, setCasOdVyhlaseni] = useState(0);
    // HO-28 H1: render-blocking error banner (persistuje, dokud uživatel nezavře nebo
    // nezkusí odeslat znovu) — chrání hasiče před tichým zahozením odpovědi.
    const [submitError, setSubmitError] = useState<{ odpoved: "JEDU" | "NEJEDU"; message: string } | null>(null);


    const odpovedMutation = useOdpovedMutation();

    // 1) Query pro aktivní poplach
    const {
        data: aktivniPoplach,
        isLoading: isLoadingPoplach,
        error: chybaAktivniPoplach,
        isFetching: isFetchingPoplach,
        refetch: refetchPoplach,
    } = useQuery<Udalost, QueryError>({
        queryKey: ["aktivniPoplach"],
        queryFn: () => JsdhNotifikaceService.getAktivniUdalost(),
        enabled: Boolean(token),
    });

    // 2) Query pro uživatelovu odpověď
    const {
        data: uzivatelovaOdpoved,
    } = useQuery<string | null, QueryError>({
        queryKey: ["uzivatelovaOdpoved", aktivniPoplach?.id, token],
        queryFn: () =>
            aktivniPoplach && token
                ? JsdhNotifikaceService.getOdpovedByPoplachIdAndToken(
                    aktivniPoplach.id,
                    token
                )
                : Promise.resolve(null),
        enabled: Boolean(aktivniPoplach?.id && token),
    });

    // 3) Query pro seznam odpovedi
    const {
        data: odpovedi,
        isFetched: isFetchedOdpovedi,
    } = useQuery<Odpoved[], QueryError>({
        queryKey: ["odpovedi", aktivniPoplach?.id],
        queryFn: () =>
            aktivniPoplach
                ? JsdhNotifikaceService.getOdpovediByPoplachId(aktivniPoplach.id)
                : Promise.resolve([]),
        enabled: Boolean(aktivniPoplach?.id),
        refetchInterval: 2000, // každých 1,5 sekundy
    });

    // odpočet
    useEffect(() => {
        if (!aktivniPoplach?.casVyhlaseni) return;

        const startTime = new Date(aktivniPoplach.casVyhlaseni).getTime();

        // inicializace ihned
        const now = new Date().getTime();
        setCasOdVyhlaseni(Math.floor((now - startTime) / 1000));

        const interval = setInterval(() => {
            const now = new Date().getTime();
            const elapsed = Math.floor((now - startTime) / 1000);
            setCasOdVyhlaseni(elapsed);
        }, 1000);

        return () => clearInterval(interval);
    }, [aktivniPoplach?.casVyhlaseni]);

    const handleOdpoved = (odpoved: "JEDU" | "NEJEDU") => {
        if (!aktivniPoplach?.id || !token) return;
        // HO-28 H1: zabráníme double-submitu a vyčistíme předchozí chybový banner.
        if (odpovedMutation.isPending) return;
        setSubmitError(null);
        odpovedMutation.mutate(
            { poplachId: aktivniPoplach.id, token, odpoved },
            {
                onError: (err) => {
                    // BE 403 / 4xx / 5xx / timeout — odpověď NEDORAZILA na BE.
                    // Cache `uzivatelovaOdpoved` zůstává nezměněna (žádný optimistic
                    // update v mutaci) → UI se NEPŘEPNE na "Již jste odpověděl".
                    // Banner níže drží chybu viditelnou bez auto-dismiss.
                    const message =
                        err instanceof Error && err.message
                            ? err.message
                            : "Server odpověď nepřijal. Zkontrolujte připojení a zkuste znovu.";
                    setSubmitError({ odpoved, message });
                },
            }
        );
    };

    // Memoizované filtrování odpovědí
    const odpovediJedu = useMemo(
        () => odpovedi?.filter((o) => o.odpoved === "JEDU") ?? [],
        [odpovedi]
    );
    const odpovediNejedu = useMemo(
        () => odpovedi?.filter((o) => o.odpoved === "NEJEDU") ?? [],
        [odpovedi]
    );

    if (!token) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: "center" }}>
                <Typography variant="h6" color="error">
                    Chybí identifikátor uživatele (token).
                </Typography>
            </Container>
        );
    }

    if (isLoadingPoplach) {
        return <OdpovedSkeleton />;
    }

    if (chybaAktivniPoplach) {
        return (
            <ErrorWithRetry
                title="Chyba při načítání"
                message="Nepodařilo se načíst informace o poplachu. Zkontrolujte připojení k internetu."
                onRetry={() => refetchPoplach()}
                isRetrying={isFetchingPoplach}
            />
        );
    }

    if (!aktivniPoplach) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: "center" }}>
                <Typography variant="h4" gutterBottom>
                    Čas pro odpověď vypršel
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Na poplach již nelze odpovědět.
                </Typography>
            </Container>
        );
    }

    const adresa = formatAdresa(aktivniPoplach);

    return (
        <Container maxWidth="sm" sx={{ mt: 2, textAlign: "center" }}>
            {/* Theme toggle in top-right corner */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 16,
                    right: 16,
                    zIndex: 1000,
                }}
            >
                <AppearanceMenu />
            </Box>

            {aktivniPoplach.pred ? (
                <Typography variant="h4" sx={{ fontWeight: "bold" }} gutterBottom>
                    {aktivniPoplach.pred.toLocaleUpperCase()}
                </Typography>
            ) : (
                <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                    align="center"
                >
                    VÝJEZD JEDNOTKY
                </Typography>
            )}

            {(aktivniPoplach.udalost || adresa) && (
                <Typography variant="h6" gutterBottom>
                    {[aktivniPoplach.udalost, adresa].filter(Boolean).join(" – ")}
                </Typography>
            )}

            {aktivniPoplach.popis && (
                <Typography variant="body1" paragraph>
                    {aktivniPoplach.popis}
                </Typography>
            )}

            <Box mt={1}>
                <CasOdVyhlaseni casVyhlaseni={aktivniPoplach.casVyhlaseni} variant={"h5"} />
            </Box>
            <Typography
                variant="h6"
                sx={{
                    mt: 0,
                    height: "2rem", // nebo jiná pevná výška
                    fontWeight: "bold",
                    // HO-28 H1: indikátor "Již jste odpověděl" se zobrazí JEN když
                    // BE potvrdil odpověď (uzivatelovaOdpoved z čerstvého fetche)
                    // a zároveň zrovna neběží nová mutace. Tím nikdy nehlásíme
                    // "potvrzeno", pokud odpověď ještě cestuje (nebo selhala).
                    opacity: uzivatelovaOdpoved && !odpovedMutation.isPending ? 1 : 0,
                    color: uzivatelovaOdpoved === "JEDU" ? "success.main" : "error.main",
                }}
            >
                Již jste odpověděl: {uzivatelovaOdpoved}
            </Typography>

            {/* HO-28 H1: render-blocking error banner — drží chybu viditelnou,
                dokud uživatel nezavře / nestiskne tlačítko znovu. Bez tohoto by
                hasič mohl přehlédnout toast a myslet si, že odpověděl. */}
            {submitError && (
                <Box mt={2} mb={1}>
                    <Alert
                        severity="error"
                        variant="filled"
                        onClose={() => setSubmitError(null)}
                        sx={{ fontSize: "1.05rem", textAlign: "left" }}
                    >
                        <AlertTitle sx={{ fontWeight: "bold" }}>
                            ODPOVĚĎ "{submitError.odpoved}" NEBYLA ODESLÁNA
                        </AlertTitle>
                        {submitError.message}
                        <br />
                        <strong>Stiskněte tlačítko {submitError.odpoved} znovu.</strong>
                    </Alert>
                </Box>
            )}


            <Box mt={2} display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Button
                    variant="contained"
                    color="success"
                    size="large"
                    // HO-28 H1: tlačítko blokujeme JEN po potvrzeném BE stavu
                    // nebo když právě běží mutace (anti-double-submit).
                    // NIKDY ne čistě optimisticky — hasič musí mít možnost zkusit
                    // znovu po selhání bez reloadu stránky.
                    disabled={uzivatelovaOdpoved === "JEDU" || odpovedMutation.isPending}
                    onClick={() => handleOdpoved("JEDU")}
                    sx={{ width: 300, height: 80, fontSize: "2.2rem" }}
                    startIcon={
                        odpovedMutation.isPending && odpovedMutation.variables?.odpoved === "JEDU"
                            ? <CircularProgress size={28} color="inherit" />
                            : undefined
                    }
                >
                    {odpovedMutation.isPending && odpovedMutation.variables?.odpoved === "JEDU"
                        ? "ODESÍLÁM…"
                        : "JEDU"}
                </Button>

                <Button
                    variant="contained"
                    color="error"
                    size="large"
                    disabled={uzivatelovaOdpoved === "NEJEDU" || odpovedMutation.isPending}
                    onClick={() => handleOdpoved("NEJEDU")}
                    sx={{ width: 300, height: 80, fontSize: "2.2rem" }}
                    startIcon={
                        odpovedMutation.isPending && odpovedMutation.variables?.odpoved === "NEJEDU"
                            ? <CircularProgress size={28} color="inherit" />
                            : undefined
                    }
                >
                    {odpovedMutation.isPending && odpovedMutation.variables?.odpoved === "NEJEDU"
                        ? "ODESÍLÁM…"
                        : "NEJEDU"}
                </Button>
            </Box>

            {/*Detail poplachu*/}
            <Box mt={5}>
                <Button
                    onClick={() => setOpen(!open)}
                    endIcon={<ExpandMoreIcon sx={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />}
                    variant="outlined"
                    fullWidth
                >
                    Zobrazit detail poplachu
                </Button>

                <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box mt={2} textAlign="left">
                        {aktivniPoplach.obec && (
                            <Typography variant="body1"><strong>Obec:</strong> {aktivniPoplach.obec}</Typography>
                        )}
                        {aktivniPoplach.cast && (
                            <Typography variant="body1"><strong>Část obce:</strong> {aktivniPoplach.cast}</Typography>
                        )}
                        {aktivniPoplach.ulice && (
                            <Typography variant="body1"><strong>Ulice:</strong> {aktivniPoplach.ulice}</Typography>
                        )}
                        {aktivniPoplach.cisloPopisne && (
                            <Typography variant="body1"><strong>Číslo popisné:</strong> {aktivniPoplach.cisloPopisne}</Typography>
                        )}
                        {aktivniPoplach.objekt && (
                            <Typography variant="body1"><strong>Objekt:</strong> {aktivniPoplach.objekt}</Typography>
                        )}
                        {aktivniPoplach.upresneni && (
                            <Typography variant="body1"><strong>Upřesnění:</strong> {aktivniPoplach.upresneni}</Typography>
                        )}

                        {/*Mapy*/}
                        <Box mt={3} mb={3} display="flex" justifyContent="center" gap={2} flexWrap="wrap">
                            {getSafeUrl(aktivniPoplach.mapyCzLink) && (
                                <Button
                                    component="a"
                                    variant="contained"
                                    href={getSafeUrl(aktivniPoplach.mapyCzLink)!}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        backgroundColor: "#1eae00", // Mapy.cz zelená
                                        color: "#fff",
                                        "&:hover": {
                                            backgroundColor: "#189400", // tmavší varianta pro hover
                                        }
                                    }}
                                >
                                    Mapy.cz
                                </Button>
                            )}
                            {getSafeUrl(aktivniPoplach.googleMapLink) && (
                                <Button
                                    component="a"
                                    variant="contained"
                                    href={getSafeUrl(aktivniPoplach.googleMapLink)!}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        backgroundColor: "#4285F4", // Google Maps modrá
                                        color: "#fff",
                                        "&:hover": {
                                            backgroundColor: "#3367D6",
                                        }
                                    }}
                                >
                                    Google Mapy
                                </Button>
                            )}
                        </Box>

                        {aktivniPoplach.dalsiTechnikaJednotek?.length > 0 && (
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.secondary" }}>
                                    Technika dalších jednotek
                                </Typography>

                                {aktivniPoplach.dalsiTechnikaJednotek.map((t, index) => (
                                    <Typography
                                        key={`dalsi-technika-${index}-${t}`}
                                        variant="body2"
                                        display="block"
                                        sx={{ lineHeight: 1.8 }}
                                    >
                                        {t}
                                    </Typography>
                                ))}
                            </Grid>
                        )}

                    </Box>
                </Collapse>
            </Box>

            {/*Počet odpovedi*/}
            {isFetchedOdpovedi && odpovedi && (
                <Box mt={4} textAlign="center">
                    <Typography variant="h6" gutterBottom>
                        Odpovědi jednotky
                    </Typography>

                    <Grid container spacing={2} justifyContent="center">
                        <Grid size={{ xs: 6 }}>
                            <Box display="flex" justifyContent="center">
                                <Typography variant="body1" color="success.main">
                                    JEDU: {odpovediJedu.length}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 6 }}>
                            <Box display="flex" justifyContent="center">
                                <Typography variant="body1" color="error.main">
                                    NEJEDU: {odpovediNejedu.length}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Detail odpovědí */}
                    <Box mt={2}>
                        <Button
                            onClick={() => setOpenDetailOdpovedi((prev) => !prev)}
                            endIcon={
                                <ExpandMoreIcon
                                    sx={{
                                        transform: openDetailOdpovedi ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.3s",
                                    }}
                                />
                            }
                            variant="outlined"
                            fullWidth
                        >
                            Zobrazit detail odpovědí
                        </Button>

                        <Collapse in={openDetailOdpovedi} timeout="auto" unmountOnExit>
                            <Box mt={2} textAlign="left">
                                {/* JEDU odpovědi */}
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "success.main", mb: 1 }}>
                                    JEDU
                                </Typography>
                                {odpovediJedu.length > 0 ? (
                                    odpovediJedu.map((o) => (
                                        <Typography key={o.id} variant="body2" sx={{ mb: 0.5 }}>
                                            {o.jmeno} – {formatDateTime(o.cas)}
                                        </Typography>
                                    ))
                                ) : (
                                    <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary", mb: 0.5 }}>
                                        žádná odpověď nezaznamenána
                                    </Typography>
                                )}

                                {/* NEJEDU odpovědi */}
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "error.main", mt: 3, mb: 1 }}>
                                    NEJEDU
                                </Typography>
                                {odpovediNejedu.length > 0 ? (
                                    odpovediNejedu.map((o) => (
                                        <Typography key={o.id} variant="body2" sx={{ mb: 0.5 }}>
                                            {o.jmeno} – {formatDateTime(o.cas)}
                                        </Typography>
                                    ))
                                ) : (
                                    <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary", mb: 0.5 }}>
                                        žádná odpověď nezaznamenána
                                    </Typography>
                                )}
                            </Box>
                        </Collapse>
                    </Box>
                </Box>
            )}


            <Typography pt={3} variant={"body1"} align={"center"}>
                Čas poplachu
            </Typography>
            <Typography variant={"body1"} align={"center"}>
                {formatDateTime(aktivniPoplach.casVyhlaseni)}
            </Typography>


        </Container>
    );
};

export default OdpovedPage;
